import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputLabel,
  Button,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Search and filter component for wiki entries
 */
const SearchAndFilter = ({ onFilter, initialFilters = {} }) => {
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm || '');
  const [selectedTags, setSelectedTags] = useState(initialFilters.tags || []);
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await wikiService.getAllTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error('Error loading tags:', error);
      }
    };

    loadTags();
  }, []);

  const handleSearch = () => {
    onFilter({
      searchTerm,
      tags: selectedTags,
    });
  };

  const handleClear = () => {
    setSearchTerm('');
    setSelectedTags([]);
    onFilter({
      searchTerm: '',
      tags: [],
    });
  };

  const handleAddTag = (tag) => {
    if (!selectedTags.includes(tag)) {
      const newTags = [...selectedTags, tag];
      setSelectedTags(newTags);
      // Automatically apply filter when tag is added
      onFilter({
        searchTerm,
        tags: newTags,
      });
    }
  };

  const handleRemoveTag = (tag) => {
    const newTags = selectedTags.filter((t) => t !== tag);
    setSelectedTags(newTags);
    // Automatically apply filter when tag is removed
    onFilter({
      searchTerm,
      tags: newTags,
    });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr' },
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search by title or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          fullWidth
          size="small"
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: wikiTheme.primary }} />,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: wikiTheme.secondary,
              fontFamily: wikiTheme.font,
              '& fieldset': {
                borderColor: wikiTheme.lightGray,
              },
              '&:hover fieldset': {
                borderColor: wikiTheme.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: wikiTheme.primary,
              },
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: wikiTheme.tertiary,
              opacity: 0.7,
            },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            onClick={handleSearch} 
            fullWidth
            sx={{
              backgroundColor: wikiTheme.primary,
              color: wikiTheme.dark,
              fontFamily: wikiTheme.font,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e0a23d',
              },
            }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<ClearIcon />}
            sx={{
              color: wikiTheme.primary,
              borderColor: wikiTheme.primary,
              fontFamily: wikiTheme.font,
              '&:hover': {
                borderColor: '#e0a23d',
                color: '#e0a23d',
              },
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      <Box>
        <InputLabel sx={{ mb: 1, color: wikiTheme.secondary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>Filter by tags:</InputLabel>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {selectedTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onDelete={() => handleRemoveTag(tag)}
              sx={{
                backgroundColor: wikiTheme.primary,
                color: wikiTheme.dark,
                fontFamily: wikiTheme.font,
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#e0a23d',
                },
              }}
            />
          ))}
        </Box>

        {availableTags.length > 0 && selectedTags.length < availableTags.length && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {availableTags
              .filter((tag) => !selectedTags.includes(tag))
              .slice(0, 8)
              .map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onClick={() => handleAddTag(tag)}
                  variant="outlined"
                  size="small"
                  clickable
                  sx={{
                    borderColor: wikiTheme.tertiary,
                    color: wikiTheme.tertiary,
                    fontFamily: wikiTheme.font,
                    '&:hover': {
                      borderColor: wikiTheme.primary,
                      color: wikiTheme.primary,
                      backgroundColor: `rgba(217, 141, 42, 0.1)`,
                    },
                  }}
                />
              ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SearchAndFilter;
